import { useDispatch, useSelector } from 'react-redux'
import date from 'src/exShared/util/date'
import { addDefaultFromThrough } from 'src/state/ducks/approvals/ApprovalReportsState'
import approvalsOperations from 'src/state/ducks/approvals/operations'
import { GetApprovalReportsFilter as ApiFilter } from 'src/state/ducks/approvals/types'
import { RootState } from 'src/state/store'
import { ApprovalReportsFilter as UIFilter } from 'src/views/types/utils/ListFilter'
import * as localStorage from './approvalReportsStorage'

/**
 * フロントで扱う絞り込み条件をAPI用クエリパラメータに変換する
 * @param filter フロントで扱う絞り込み条件
 * @returns API用のクエリパラメータ
 */
export const convertToApiFilter = (filter?: UIFilter): ApiFilter => {
  if (!filter) {
    return {}
  }
  const reportName = filter.reportName
    ? { $like: filter.reportName }
    : undefined
  const reportDate =
    filter.from && filter.through
      ? {
          $gte: date.startOfDay(date.parseYYYYMMDD(filter.from)),
          $lte: date.endOfDay(date.parseYYYYMMDD(filter.through)),
        }
      : undefined
  const isInvalid = filter.isInvalid
    ? { $in: [filter.isInvalid ?? 0] }
    : undefined
  const user =
    filter.user !== undefined && filter.user.length > 0
      ? { $like: filter.user }
      : undefined
  return { reportName, reportDate, isInvalid, assignee: { user } }
}

const convertToUIFilter = (filter: ApiFilter): UIFilter => {
  const reportName = filter.reportName?.$like
  const reportDate =
    filter.reportDate?.$gte && filter.reportDate?.$lte
      ? {
          from: date.formatYYYYMMDD(filter.reportDate.$gte),
          through: date.formatYYYYMMDD(filter.reportDate.$lte),
        }
      : undefined
  const isInvalid = filter.isInvalid?.$in?.[0]
  const user = filter.assignee?.user?.$like
  return {
    reportName,
    from: reportDate?.from,
    through: reportDate?.through,
    isInvalid,
    user,
  }
}

/**
 * 承認レポート一覧の絞り込み機能を提供するhook
 * localStorageに保存された絞り込み状態を管理します
 * API用のクエリパラメータの状態管理も行います
 */
export const useApprovalReportsFilter = () => {
  const dispatch = useDispatch()
  const { filter: state } = useSelector(
    (state: RootState) => state.approvalsState.getApprovalReports,
  )
  const { filter: localstorage } = localStorage.getStorageData()

  /**
   * 絞り込みを更新する
   * 1. LocalStorageに保存
   * 2. API用絞り込みにも更新する
   * @param filter フロント用絞り込み
   */
  const setFilter = (filter?: UIFilter) => {
    localStorage.saveFilter(filter)
    // filter期間が未設定の場合はデフォルト値を設定
    const filterWithDateRange = addDefaultFromThrough(filter)
    const newApiFilter = convertToApiFilter(filterWithDateRange)
    approvalsOperations.updateFilter(dispatch, newApiFilter)
  }

  /**
   * フロント絞り込みを部分的に更新する
   * @param partialFilter 更新する絞り込みの一部
   */
  const updateFilter = (partialFilter: Partial<UIFilter>) => {
    const currentFilter = localstorage || {}
    const updatedFilter = {
      ...currentFilter,
      ...partialFilter,
    }
    setFilter(updatedFilter)
  }

  const resetFilter = () => {
    setFilter()
  }

  // 日付絞り込みが設定されているかどうか
  const isDateFilterChanged = (): boolean => {
    return !!(localstorage?.from && localstorage?.through)
  }

  return {
    // API用絞り込み
    apiFilter: state,
    // UI用絞り込み
    uiFilter: state ? convertToUIFilter(state) : {},
    updateFilter,
    resetFilter,
    // ヘルパー関数
    isDateFilterChanged,
  }
}
